import React from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Segmented,
  Select,
  Space,
  Tooltip,
  Typography
} from "antd";
import { CopyOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import LengthInput from "./LengthInput";
import RoomSketch from "./RoomSketch";
import { closingWall } from "./geometry";
import { formatLength, formatM, formatM2, formatM3 } from "./format";

const LEVELS = ["KG", "EG", "OG", "1. OG", "2. OG", "DG"];
const TURNS = [90, -90, 45, -45, 135, -135];
const TURN_LABELS = {
  90: "Right 90°",
  "-90": "Left 90°",
  45: "Right 45°",
  "-45": "Left 45°",
  135: "Right 135°",
  "-135": "Left 135°"
};
const OPENING_LABELS = { door: "Door", window: "Window", other: "Other opening" };
const TARGET_LABELS = { floor: "Floor", wall: "Walls", ceiling: "Ceiling" };

const WARNING_TEXT = {
  MISSING_DIMENSIONS: () => "Enter all dimensions of the floor.",
  IMPOSSIBLE_SHAPE: () =>
    "These walls and the diagonal cannot form a room. One of the values is wrong.",
  DIAGONAL_MISMATCH: () =>
    "The second diagonal should be {{expected}} according to the other values, but {{measured}} was measured. One of the values is probably wrong.",
  NOT_CLOSED: () =>
    "The walls end {{gap}} away from the starting corner. Check the lengths and corners.",
  NO_HEIGHT: () => "Enter the room height to get the wall areas.",
  NEGATIVE_FLOOR: () => "More was deducted from the floor than it has.",
  NEGATIVE_CEILING: () => "More was deducted from the ceiling than it has.",
  NEGATIVE_WALL: () => "More was deducted from the walls than they have."
};

const Field = ({ label, children, extra }) => (
  <Form.Item label={label} extra={extra} className="ms-field">
    {children}
  </Form.Item>
);

const RoomCard = ({ room, summary, onChange, onRemove, onDuplicate, onMeasure, readOnly }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const set = patch => onChange({ ...room, ...patch });
  // The camera button of a field: the tool gets a label and a setter for the result.
  const measure = label => (onMeasure ? apply => onMeasure(label, apply) : undefined);
  // `label` is already translated.
  const length = (label, value, apply, extra) => (
    <Field label={label} extra={extra}>
      <LengthInput
        value={value}
        readOnly={readOnly}
        ariaLabel={label}
        onChange={apply}
        onMeasure={measure(`${room.name || t("Room")} · ${label}`)}
      />
    </Field>
  );

  const setListItem = (key, index, patch) =>
    set({ [key]: room[key].map((item, i) => (i === index ? { ...item, ...patch } : item)) });
  const removeListItem = (key, index) => set({ [key]: room[key].filter((_, i) => i !== index) });

  const setSide = (index, mm) => set({ sides: room.sides.map((s, i) => (i === index ? mm : s)) });

  const closing =
    room.shape === "polygon" ? closingWall(room.walls.filter(w => w.length > 0)) : null;
  const addClosingWall = () => {
    const walls = room.walls.filter(w => w.length > 0);
    walls[walls.length - 1] = { ...walls[walls.length - 1], turn: closing.turn };
    set({ walls: [...walls, { length: closing.length, turn: 90 }] });
  };

  const changeShape = shape => {
    // Carry the rectangle over, so switching to a more exact shape keeps what was measured.
    const patch = { shape };
    if (shape === "quad" && !room.sides.some(Boolean) && room.length && room.width) {
      patch.sides = [room.length, room.width, room.length, room.width];
    }
    if (shape === "polygon" && !room.walls.length) {
      patch.walls =
        room.length && room.width
          ? [room.length, room.width, room.length, room.width].map(l => ({ length: l, turn: 90 }))
          : [0, 0, 0, 0].map(() => ({ length: 0, turn: 90 }));
    }
    set(patch);
  };

  const figures = summary?.figures;
  const warningText = warning =>
    t(WARNING_TEXT[warning.code]?.() || warning.code, {
      gap: formatLength(warning.gap, lng),
      expected: formatLength(warning.expected, lng),
      measured: formatLength(warning.measured, lng)
    });

  return (
    <Form layout="vertical" component="div" colon={false} className="ms-room">
      <Row gutter={[12, 0]}>
        <Col xs={24} md={12}>
          <Field label={t("Room")}>
            <Input
              value={room.name}
              readOnly={readOnly}
              placeholder={t("e.g. Living room")}
              onChange={event => set({ name: event.target.value })}
            />
          </Field>
        </Col>
        <Col xs={12} md={6}>
          <Field label={t("Floor level")}>
            <AutoComplete
              value={room.level}
              disabled={readOnly}
              options={LEVELS.map(value => ({ value }))}
              onChange={value => set({ level: value })}
            />
          </Field>
        </Col>
        <Col xs={12} md={6}>
          {length(t("Height"), room.height, mm => set({ height: mm }))}
        </Col>
      </Row>

      <Segmented
        block
        className="ms-shape"
        value={room.shape}
        disabled={readOnly}
        onChange={changeShape}
        options={[
          { value: "rectangle", label: t("Rectangle") },
          { value: "quad", label: t("4 walls + diagonal") },
          { value: "polygon", label: t("Free shape") }
        ]}
      />

      <Row gutter={[16, 8]}>
        <Col xs={24} lg={14}>
          {room.shape === "rectangle" && (
            <Row gutter={[12, 0]}>
              <Col xs={12}>{length(t("Length"), room.length, mm => set({ length: mm }))}</Col>
              <Col xs={12}>{length(t("Width"), room.width, mm => set({ width: mm }))}</Col>
            </Row>
          )}

          {room.shape === "quad" && (
            <>
              <Typography.Paragraph type="secondary" className="ms-hint">
                {t(
                  "For rooms that are not exactly square: measure all four walls and the diagonal from corner A to C. The second diagonal is a check."
                )}
              </Typography.Paragraph>
              <Row gutter={[12, 0]}>
                {["A–B", "B–C", "C–D", "D–A"].map((label, index) => (
                  <Col xs={12} key={label}>
                    {length(`${t("Wall")} ${label}`, room.sides[index], mm => setSide(index, mm))}
                  </Col>
                ))}
                <Col xs={12}>
                  {length(t("Diagonal A–C"), room.diagonal, mm => set({ diagonal: mm }))}
                </Col>
                <Col xs={12}>
                  {length(
                    t("Diagonal B–D (check)"),
                    room.diagonal2,
                    mm => set({ diagonal2: mm }),
                    summary?.expectedDiagonal2
                      ? `${t("Expected")}: ${formatLength(summary.expectedDiagonal2, lng)}`
                      : null
                  )}
                </Col>
              </Row>
            </>
          )}

          {room.shape === "polygon" && (
            <>
              <Typography.Paragraph type="secondary" className="ms-hint">
                {t(
                  "Walk around the room: enter each wall and the corner at its end. The plan shows whether the walls meet again."
                )}
              </Typography.Paragraph>
              {room.walls.map((wall, index) => (
                <Row gutter={[8, 0]} key={index} align="bottom" wrap={false}>
                  <Col flex="auto">
                    {length(`${t("Wall")} ${index + 1}`, wall.length, mm =>
                      setListItem("walls", index, { length: mm })
                    )}
                  </Col>
                  <Col flex="130px">
                    <Field label={t("Then")}>
                      <Select
                        value={TURNS.includes(wall.turn) ? wall.turn : "custom"}
                        disabled={readOnly}
                        onChange={value =>
                          setListItem("walls", index, { turn: value === "custom" ? 60 : value })
                        }
                        options={[
                          ...TURNS.map(value => ({
                            value,
                            label: t(TURN_LABELS[value])
                          })),
                          { value: "custom", label: t("Other angle") }
                        ]}
                      />
                    </Field>
                  </Col>
                  {!TURNS.includes(wall.turn) && (
                    <Col flex="96px">
                      <Field label="°">
                        <InputNumber
                          value={wall.turn}
                          min={-179}
                          max={180}
                          step={0.5}
                          disabled={readOnly}
                          onChange={value =>
                            setListItem("walls", index, { turn: Number(value) || 0 })
                          }
                        />
                      </Field>
                    </Col>
                  )}
                  {!readOnly && (
                    <Col flex="44px">
                      <Form.Item className="ms-field" label=" ">
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          aria-label={t("Delete")}
                          disabled={room.walls.length <= 3}
                          onClick={() => removeListItem("walls", index)}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              ))}
              {!readOnly && (
                <Space wrap>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => set({ walls: [...room.walls, { length: 0, turn: 90 }] })}
                  >
                    {t("Add wall")}
                  </Button>
                  {closing && room.walls.filter(w => w.length > 0).length >= 2 && (
                    <Tooltip
                      title={t("Adds the wall back to the starting corner: {{length}}", {
                        length: formatLength(closing.length, lng)
                      })}
                    >
                      <Button onClick={addClosingWall}>{t("Close the shape")}</Button>
                    </Tooltip>
                  )}
                </Space>
              )}
            </>
          )}
        </Col>
        <Col xs={24} lg={10}>
          <RoomSketch room={room} summary={summary} />
        </Col>
      </Row>

      {summary?.warnings?.map(warning => (
        <Alert
          key={warning.code}
          className="ms-warning"
          type={
            warning.code === "NO_HEIGHT" || warning.code === "MISSING_DIMENSIONS"
              ? "info"
              : "warning"
          }
          showIcon
          message={warningText(warning)}
        />
      ))}

      <Divider orientation="left" plain className="ms-divider">
        {t("Doors, windows and openings")}
      </Divider>
      {room.openings.map((opening, index) => (
        <div className="ms-item" key={index}>
          <Row gutter={[8, 0]}>
            <Col xs={12} md={5}>
              <Field label={t("Type")}>
                <Select
                  value={opening.kind}
                  disabled={readOnly}
                  onChange={kind => setListItem("openings", index, { kind })}
                  options={Object.entries(OPENING_LABELS).map(([value, label]) => ({
                    value,
                    label: t(label)
                  }))}
                />
              </Field>
            </Col>
            <Col xs={12} md={3}>
              <Field label={t("Count")}>
                <InputNumber
                  min={1}
                  max={999}
                  value={opening.count}
                  disabled={readOnly}
                  onChange={count => setListItem("openings", index, { count: count || 1 })}
                />
              </Field>
            </Col>
            <Col xs={12} md={5}>
              {length(t("Width"), opening.width, mm =>
                setListItem("openings", index, { width: mm })
              )}
            </Col>
            <Col xs={12} md={5}>
              {length(t("Height"), opening.height, mm =>
                setListItem("openings", index, { height: mm })
              )}
            </Col>
            <Col xs={18} md={5}>
              {length(t("Reveal depth"), opening.depth, mm =>
                setListItem("openings", index, { depth: mm })
              )}
            </Col>
            {!readOnly && (
              <Col xs={6} md={1}>
                <Form.Item className="ms-field" label=" ">
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    aria-label={t("Delete")}
                    onClick={() => removeListItem("openings", index)}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </div>
      ))}
      {!readOnly && (
        <Space wrap className="ms-add-row">
          {["door", "window", "other"].map(kind => (
            <Button
              key={kind}
              icon={<PlusOutlined />}
              onClick={() =>
                set({
                  openings: [
                    ...room.openings,
                    { kind, label: "", width: 0, height: 0, count: 1, depth: 0 }
                  ]
                })
              }
            >
              {t(OPENING_LABELS[kind])}
            </Button>
          ))}
        </Space>
      )}

      <Divider orientation="left" plain className="ms-divider">
        {t("Deductions and additions")}
      </Divider>
      {room.adjustments.map((item, index) => (
        <div className="ms-item" key={index}>
          <Row gutter={[8, 0]}>
            <Col xs={24} md={6}>
              <Field label={t("What")}>
                <Input
                  value={item.label}
                  readOnly={readOnly}
                  placeholder={t("e.g. Pillar, chimney, niche")}
                  onChange={event =>
                    setListItem("adjustments", index, { label: event.target.value })
                  }
                />
              </Field>
            </Col>
            <Col xs={12} md={4}>
              <Field label={t("Surface")}>
                <Select
                  value={item.target}
                  disabled={readOnly}
                  onChange={target => setListItem("adjustments", index, { target })}
                  options={Object.entries(TARGET_LABELS).map(([value, label]) => ({
                    value,
                    label: t(label)
                  }))}
                />
              </Field>
            </Col>
            <Col xs={12} md={4}>
              <Field label={t("Effect")}>
                <Select
                  value={item.sign}
                  disabled={readOnly}
                  onChange={sign => setListItem("adjustments", index, { sign })}
                  options={[
                    { value: -1, label: t("Deduct") },
                    { value: 1, label: t("Add") }
                  ]}
                />
              </Field>
            </Col>
            <Col xs={12} md={4}>
              {length(t("Length"), item.length, mm =>
                setListItem("adjustments", index, { length: mm })
              )}
            </Col>
            <Col xs={12} md={4}>
              {length(t("Width"), item.width, mm =>
                setListItem("adjustments", index, { width: mm })
              )}
            </Col>
            <Col xs={18} md={1}>
              <Field label={t("Count")}>
                <InputNumber
                  min={1}
                  max={999}
                  value={item.count}
                  disabled={readOnly}
                  onChange={count => setListItem("adjustments", index, { count: count || 1 })}
                />
              </Field>
            </Col>
            {!readOnly && (
              <Col xs={6} md={1}>
                <Form.Item className="ms-field" label=" ">
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    aria-label={t("Delete")}
                    onClick={() => removeListItem("adjustments", index)}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </div>
      ))}
      {!readOnly && (
        <Button
          icon={<PlusOutlined />}
          className="ms-add-row"
          onClick={() =>
            set({
              adjustments: [
                ...room.adjustments,
                { label: "", target: "floor", sign: -1, length: 0, width: 0, count: 1 }
              ]
            })
          }
        >
          {t("Deduction or addition")}
        </Button>
      )}

      <Field label={t("Notes")}>
        <Input.TextArea
          value={room.notes}
          readOnly={readOnly}
          autoSize={{ minRows: 1, maxRows: 6 }}
          onChange={event => set({ notes: event.target.value })}
        />
      </Field>

      {figures && summary.valid && (
        <dl className="ms-figures">
          <div>
            <dt>{t("Floor")}</dt>
            <dd>{formatM2(figures.floorNet, lng)}</dd>
          </div>
          <div>
            <dt>{t("Ceiling")}</dt>
            <dd>{formatM2(figures.ceilingNet, lng)}</dd>
          </div>
          <div>
            <dt>{t("Walls (net)")}</dt>
            <dd>{room.height ? formatM2(figures.wallNet, lng) : "–"}</dd>
          </div>
          <div>
            <dt>{t("Perimeter")}</dt>
            <dd>{formatM(figures.perimeter, lng)}</dd>
          </div>
          <div>
            <dt>{t("Skirting")}</dt>
            <dd>{formatM(figures.skirting, lng)}</dd>
          </div>
          <div>
            <dt>{t("Volume")}</dt>
            <dd>{room.height ? formatM3(figures.volume, lng) : "–"}</dd>
          </div>
          {room.height > 0 && (
            <div className="ms-figures-wide">
              <dt>{t("Walls")}</dt>
              <dd>
                {formatM2(figures.wallGross, lng)} − {formatM2(figures.openingsDeducted, lng)}{" "}
                {t("openings")}
                {figures.reveals > 0 && ` + ${formatM2(figures.reveals, lng)} ${t("reveals")}`}
                {figures.openingsKept > 0 &&
                  ` · ${t("not deducted")}: ${formatM2(figures.openingsKept, lng)}`}
              </dd>
            </div>
          )}
        </dl>
      )}

      {!readOnly && (
        <Space className="ms-room-actions">
          <Button icon={<CopyOutlined />} onClick={onDuplicate}>
            {t("Duplicate")}
          </Button>
          <Popconfirm
            title={t("Delete this room?")}
            okText={t("Delete")}
            okButtonProps={{ danger: true }}
            cancelText={t("Cancel")}
            onConfirm={onRemove}
          >
            <Button danger icon={<DeleteOutlined />}>
              {t("Delete room")}
            </Button>
          </Popconfirm>
        </Space>
      )}
    </Form>
  );
};

export default RoomCard;
